import { Injectable } from '@angular/core';
import {
  AuthSession,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  signIn,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { ApplicationConfig } from '../../core/config/application.config';
import { AuthToken } from '../../domain/auth/model/auth-token';
import {
  AuthProvider,
  LoginResult,
} from '../../domain/auth/model/auth-provider';

@Injectable({ providedIn: 'root' })
export class CognitoAuthService implements AuthProvider {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: ApplicationConfig.cognito!.userPoolId,
          userPoolClientId: ApplicationConfig.cognito!.userPoolWebClientId,
          loginWith: {
            email: true,
          },
        },
      },
    });
  }

  public async login(username: string, password: string): Promise<LoginResult> {
    return signIn({ username, password }).then(result => {
      let nextStep = 'DONE';

      switch (result.nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          nextStep = 'SET_PASSWORD';
          break;
      }

      return { nextStep } as LoginResult;
    });
  }

  public async setPassword(password: string): Promise<any> {
    return await confirmSignIn({
      challengeResponse: password,
    });
  }

  public async changePassword(
    currentPassword: string,
    password: string
  ): Promise<any> {
    return updatePassword({
      oldPassword: currentPassword,
      newPassword: password,
    });
  }

  public async confirmResetPassword(
    username: string,
    confirmationCode: string,
    password: string
  ): Promise<any> {
    return confirmResetPassword({
      username,
      confirmationCode,
      newPassword: password,
    });
  }

  public resetPassword(username: string): Promise<any> {
    return resetPassword({ username });
  }

  public async logout() {
    return signOut({ global: true });
  }

  public async refresh(): Promise<AuthToken> {
    return fetchAuthSession({ forceRefresh: true }).then(this.generateToken);
  }

  public async generateToken(session?: AuthSession): Promise<AuthToken> {
    const { tokens } = session || (await fetchAuthSession());
    const { accessToken } = tokens!;

    return {
      accessToken: accessToken!.toString(),
      email: accessToken!.payload['email'],
    } as AuthToken;
  }
}
